import type {
  GridReadyEvent,
  RangeSelectionChangedEvent,
  NavigateToNextCellParams,
  CellPosition,
  VirtualColumnsChangedEvent,
  Column,
  ColumnEvent,
  ProcessHeaderForExportParams,
  BodyScrollEvent,
  IRowNode,
  ProcessDataFromClipboardParams,
} from '@ag-grid-community/core';
import ViewportDatasource from './ViewportDataSource';
import { TIME } from '../../../utils/Domain/Constants';
import _, { get, head, isEmpty, isNil, isObject, last } from 'lodash';
import { MOVE_DIRECTION_LOOKUP } from '../../../utils/Component/AgGrid/keyBoardBindings';
import { AgPivot } from './AgPivot';
import { HierarchyMember } from '../../../pivot/Member';
import { Field } from '../../../pivot/Field';
import { GRID_SAVING, GRID_ERROR, GRID_REFRESHING } from 'src/state/scope/Scope.types';
import { getCurrentVirtualRowIndicies, getRowBoundsFromGridApi } from 'src/utils/Component/Pivot';
import { CELL_TAG_DISABLED, PivotCell } from 'src/pivot/PivotCell';
import { FAUX_CELL } from 'src/utils/interface/PivotCell.tags';

export function handleGridReady(this: AgPivot, params: GridReadyEvent) {
  // assign private vars to callback
  this.gridApi = params.api;

  // setup the viewportDatasource
  if (!this.viewportDataSource && this.state.manager) {
    this.viewportDataSource = new ViewportDatasource({
      manager: this.state.manager,
      gridApi: this.gridApi,
      initialColumnIndicies: getCurrentVirtualRowIndicies(this.gridApi),
      imperativeAsyncRefreshOnly: false,
    });
    // this.gridApi.setViewportDatasource(this.viewportDataSource);
    this.gridApi.setGridOption('viewportDatasource', this.viewportDataSource);
    this.viewportDataSource.onRowCountChanged();
  }

  // attach and fire one rowData call after first mount to refresh the css
  this.gridApi.addEventListener('cellsReturned', this.redrawRowsAfterModelUpdate);

  // attach listener to refresh floating row after cells returned
  this.gridApi.addEventListener('cellsReturned', this.resetFloatingTextAfterCellsReturned);

  // select the first cell when the grid first loads
  const firstIntersection = this.state.manager.config.getColIntersections()[0];
  const firstCol = firstIntersection.getFieldListAsPipeString();

  if (params.api.getAllDisplayedColumns() && params.api.getAllDisplayedColumns().length > 0) {
    // focus the first cell on load, and if the columns are loaded
    this.gridApi.setFocusedCell(0, firstCol);
  }

  // bind collapsing time columns on grid ready
  const isFirstColTime = get(firstIntersection, 'fields[0].member.parent.id') === TIME;
  if (isFirstColTime) {
    const timeFields = this.state.manager.config.getColGroups()[0].getVisible();
    const cols = params.api.getAllDisplayedVirtualColumns();
    bindColumnToClick(cols, timeFields, this.eventCache);
  }

  this.setState({
    ready: true,
    loading: true,
  });
}

export function handleBindClickToColumns(this: AgPivot) {
  const timeFields = this.state.manager.config.getColGroups()[0].getVisible();
  const api = this.gridApi;
  if (!api) {
    return;
  }
  const cols = api.getColumns() || [];
  bindColumnToClick(cols, timeFields, this.eventCache);
}

const bindColumnToClick = (
  cols: Column[],
  timeFields: Field[],
  eventCache: {
    [key: string]: (evt: ColumnEvent) => void;
  },
) => {
  // this if for collapsing of time columns
  cols.forEach((col) => {
    const evt = function (evt: ColumnEvent) {
      // @ts-ignore
      // total hack around the fact that sortChanged fires multiple times per column click
      // note that this accesses a private interface and it could break
      if (evt.api.eventService.asyncFunctionsQueue.length === 0) {
        onSortChanged(evt, timeFields);
      }
    };
    col.addEventListener('sortChanged', evt);
    eventCache[col.getColId()] = evt;
  });
};

function onSortChanged(event: ColumnEvent, timeFields: Field[]) {
  function returnNameCheckChildren(mem: HierarchyMember): string[] {
    let name: string[] = [mem.name];
    if (mem.children) {
      const childIds = _.flattenDeep(mem.children.map(returnNameCheckChildren)) as string[];
      name = name.concat(childIds);
    }
    return name;
  }

  const column = event.column;
  if (!column) {
    return;
  }

  const colHeaderName = column.getColDef().headerName;
  const getAllDisplayedColumns = event.api.getAllGridColumns();

  let timeIdsToToggle: string[] = [];
  const clickedField = timeFields!.find((fld) => fld.member.name === colHeaderName);
  if (clickedField && clickedField.member.children) {
    timeIdsToToggle = _.flattenDeep(clickedField.member.children.map(returnNameCheckChildren)) as string[];
    const isVisible = clickedField.member.areChildrenColumnsVisible;
    const findColumnIds = getAllDisplayedColumns
      .map((col) => {
        const columnHeaderName = col.getColDef().headerName;
        return columnHeaderName && timeIdsToToggle.includes(columnHeaderName) ? col.getColId() : '';
      })
      .filter((colId) => !isEmpty(colId));
    clickedField.member.areChildrenColumnsVisible = !isVisible;
    event.api.setColumnsVisible(findColumnIds, !isVisible);
  }
}

export function handlePaste(this: AgPivot, params: ProcessDataFromClipboardParams): string[][] | null {
  // custom paste so we can override ag-grid default action
  const manager = this.state.manager;
  const pasteContent = params.data;
  const selectionRange = this.selectionRange;
  const cellsToSend: any[] = [];
  const updatePromises: Promise<any>[] = [];

  const api = this.gridApi;
  if (!api) {
    return null;
  }

  // refresh stale indicator
  this.props.onUpdateGridAsyncState(GRID_SAVING);
  if (this.props.onDataChange) {
    this.props.onDataChange(this);
  }

  for (let r = selectionRange.startY; r <= selectionRange.endY; r++) {
    const columns = pasteContent[(r - selectionRange.startY) % pasteContent.length];
    for (let c = selectionRange.startX; c <= selectionRange.endX; c++) {
      const cell = manager.getCell(r, c);
      if (cell && !cell.hasTag(CELL_TAG_DISABLED) && !cell.hasLock()) {
        let value;
        const pasteCell = cell.clone();
        value = columns[(c - selectionRange.startX) % columns.length];
        pasteCell.value = value;
        cellsToSend.push(pasteCell);
      }
    }
  }

  updatePromises.push(manager.updateCells(cellsToSend));

  Promise.all(updatePromises)
    .then(() => {
      this.props.onUpdateGridAsyncState(GRID_REFRESHING);
      // refresh stale indicator
      if (this.props.onDataChange) {
        this.props.onDataChange(this);
      }
      this.refreshCells();
      api.addEventListener('cellsReturned', this.handleGridHasRefreshed);
      if (this.props.onDataModification) {
        this.props.onDataModification();
      }

      // reset the focus cell so keyboard nav doesn't break
      const lastFocusedCell = api.getFocusedCell();
      if (!lastFocusedCell) {
        return;
      }
      api.setFocusedCell(lastFocusedCell.rowIndex, lastFocusedCell.column);
    })
    .catch((_err) => {
      this.props.onUpdateGridAsyncState(GRID_ERROR);
    });

  return null;
}

export const handleProcessHeaderForClipboard = (params: ProcessHeaderForExportParams) => {
  return params.column.getColId();
};

export function handleRangeSelectionChange(this: AgPivot, event: RangeSelectionChangedEvent) {
  // only supporting one selection right now, ignoring the rest
  const colOffset = this.state.manager.config.getRowGroupsCount();
  const api = this.gridApi;
  if (!api) {
    return;
  }
  const currentRangeSelections = api.getCellRanges();
  if (!isNil(currentRangeSelections) && !_.isEmpty(currentRangeSelections)) {
    const newRangeSelection = currentRangeSelections[0];
    const { startRow, endRow, columns } = newRangeSelection;

    if (!startRow || !endRow || isEmpty(columns)) {
      return;
    }

    const endColumn = last(columns);
    // deriving startColumn here instead of using the one from the event,
    // as the event sets `startColumn` to be the first column the user selected, not the first column
    // in column order.  We need them in column order, so use the first in the `columns` object
    const startColumn = head(columns);

    if (isNil(startColumn) || isNil(endColumn)) {
      return;
    }

    const startColIndex = api.getAllGridColumns().findIndex((c) => c == startColumn);
    const endColIndex = api.getAllGridColumns().findIndex((c) => c == endColumn);

    // need to apply an offset to make the ag-grid api fit the remote api
    const startColNum = startColIndex - colOffset;
    const endColNum = endColIndex - colOffset;

    this.selectionRange = {
      startX: startColNum,
      endX: endColNum,
      startY: startRow.rowIndex,
      endY: endRow.rowIndex,
    };
    if (this.props.onSelectionChange) {
      this.props.onSelectionChange(this.getSelectedCells());
    }
  }
}

export function handleNavigateToNextCell(this: AgPivot, params: NavigateToNextCellParams): CellPosition {
  // merging the event streams together here
  // maybe this is too DRY, split them and repeat if it causes issues
  const event: KeyboardEvent | null = params.event;
  const previousCellPosition: CellPosition = params.previousCellPosition;

  if (!event) {
    // don't move if the event is missing for some reason
    return this.getCellFromRowCol(0, 0, previousCellPosition);
  }

  event.preventDefault();
  event.stopPropagation();

  let [moveRow, moveColumn] = MOVE_DIRECTION_LOOKUP[event.which];
  if (event.shiftKey) {
    // flip it
    moveRow = moveRow * -1;
    moveColumn = moveColumn * -1;
  }

  const nextCellDef: CellPosition = this.getCellFromRowCol(moveRow, moveColumn, previousCellPosition);

  return nextCellDef;
}

export function handleKeyboardNavigateToNextCell(this: AgPivot, params: React.KeyboardEvent): void {
  // merging the event streams together here
  // maybe this is too DRY, split them and repeat if it causes issues
  const event: React.KeyboardEvent = params;
  const api = this.gridApi;
  if (!api) {
    return;
  }
  const previousCellPosition = api.getFocusedCell();

  if (!previousCellPosition) {
    // don't move if the cell is missing for some reason
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  // This allows users to continue using WASD keys for navigation, until they start entering numbers to edit
  api.stopEditing();

  let [moveRow, moveColumn] = MOVE_DIRECTION_LOOKUP[event.which];
  if (event.shiftKey) {
    // flip it
    moveRow = moveRow * -1;
    moveColumn = moveColumn * -1;
  }

  const nextCellDef = this.getCellFromRowCol(moveRow, moveColumn, previousCellPosition);

  api.clearRangeSelection();
  api.clearFocusedCell();

  api.setFocusedCell(nextCellDef.rowIndex, nextCellDef.column, undefined);

  api.addCellRange({
    rowStartIndex: nextCellDef.rowIndex,
    rowEndIndex: nextCellDef.rowIndex,
    columnStart: nextCellDef.column,
    columnEnd: nextCellDef.column,
  });
}

export function handleVirtualColumnsChanged(this: AgPivot, params: VirtualColumnsChangedEvent) {
  if (!params.api) {
    return;
  }
  // TODO: this causes a memory leak because we don't clean up the columns event correctly
  // because the events for catching a column removed are awful
  const timeFields = this.state.manager.config.getColGroups()[0].getVisible();
  bindColumnToClick(params.api.getAllDisplayedVirtualColumns(), timeFields, this.eventCache);

  // column pagination stuff
  const currentRowIndicies = getRowBoundsFromGridApi(params.api);
  this.viewportDataSource!.updateColumnIndicies(getCurrentVirtualRowIndicies(params.api), currentRowIndicies);

  // tries to float: right the columGroup headerName
  // this functionality has been requested with ag-grid directly,
  // this is a hack for now
  // gridPanel is a private interface, it could break
  // @ts-ignore
  const scrollLeft = params.api.gridBodyCtrl.eGridBody.scrollLeft;
  const columnIndexToCheck = 2; // arrived at this based on trial and error
  const { config } = this.state.manager;

  // clear the other first rows
  document
    .querySelectorAll('.ag-header-group-cell.ag-header-group-cell-with-group')
    .forEach((elem) => elem.classList.remove('first'));

  // don't do it if you're already scroll to the left, and if there aren't multiple colGroups
  if (scrollLeft > 0 && config.getColGroups().length > 1) {
    // manually add a class to the first row when scrolling left
    const id = params.api.getAllDisplayedVirtualColumns()[columnIndexToCheck].getParent().getColGroupDef()!.groupId;
    const querySelector = `[col-id="${id}_0"]`;
    const colElem = document.querySelector(querySelector);
    if (colElem) {
      colElem.classList.add('first');
    }
  }
}

const removeStyleTags = (p: HTMLParagraphElement) => {
  // that could conflict with future useage of style tags on these elements
  p.style.removeProperty('top');
};

export function handleBodyScroll(this: AgPivot, params: BodyScrollEvent) {
  if (!this.gridApi) {
    return;
  }

  // this is a private ag-grid interface.  We use it here because it's the easiest way
  // to get the first "in view" row, irrespective of virtualization and rowBuffer
  // this interface may change when we upgrade ag-grid.  getFirstRenderedRow gives the
  // first virtualized row, which usually isn't in view
  // @ts-ignore
  // add 15 pixels to ensure we're getting a row that's actually in view
  const rowIndexFromPixel = this.gridApi.rowModel.getRowIndexAtPixel(params.top + 15);
  const firstRowInView = this.gridApi.getDisplayedRowAtIndex(rowIndexFromPixel);
  if (!firstRowInView) {
    return;
  }
  const rowData = firstRowInView.data as PivotCell[];

  if (!firstRowInView || !rowData || !Array.isArray(rowData)) {
    // This shouldn't happen, but it can despite what the types say
    return;
  }

  const fauxCells = rowData.filter((cell) => cell.tags.includes(FAUX_CELL));
  const newTopLeftMemberNames = fauxCells.map((cell) => cell.value ?? cell.displayValue);

  // Only update if the array has changed
  // TODO maybe put this in `shouldComponentUpdate`
  if (JSON.stringify(newTopLeftMemberNames) !== JSON.stringify(this.state.topLeftMemberName)) {
    this.setState({ topLeftMemberName: newTopLeftMemberNames });
  }
  const floater = document.documentElement.querySelector('#mfp-floating-row') as HTMLElement;
  const pinnedContainer = document.querySelector('.ag-pinned-left-cols-container') as HTMLElement | null;
  if (!floater || !pinnedContainer) return;

  // calculate the widths of the faux cells based on the current widths of the actual columns, fallback to original way
  const leftColumns = this.gridApi.getDisplayedLeftColumns();
  const widths = fauxCells.map((_, i) => leftColumns[i]?.getActualWidth() ?? this.getRowHeaderWidth(i));

  let left = 0;

  fauxCells.forEach((rowGroup, idx) => {
    const nthRowGroup = floater.querySelector(`:nth-child(${idx + 1})`) as HTMLElement; // idx + 1 because `nth-child` is 1 indexed
    if (nthRowGroup) {
      // extract depth level number from rowGroup
      const depthTag = (rowGroup.tags || []).find((t) => t.startsWith('depth-'));
      const depth = depthTag ? parseInt(depthTag.split('-')[1], 10) : 1;
      // if depth > 1 is child group, indent 11px
      const childIndent = depth > 1 ? (depth - 1) * 11 : 0;
      const currWidth = widths[idx];
      nthRowGroup.style.width = `${currWidth}px`;
      nthRowGroup.style.left = `${left}px`;
      nthRowGroup.style.paddingLeft = `${childIndent}px`;
      left += currWidth;
    }
  });
}
